import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { formatBytes, formatTimeAgo } from '../utils/helpers';
import { Monitor, ListVideo, FolderOpen, Calendar, Activity, Wifi, WifiOff } from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/dashboard/stats');
      setStats(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); const i = setInterval(fetchStats, 15000); return () => clearInterval(i); }, []);

  if (loading) return <div className="loading-page"><span className="spinner" /><span>Loading dashboard...</span></div>;
  if (!stats) return <div className="loading-page">Failed to load dashboard</div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <div className="page-header-sub">System overview and activity</div>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card-label"><Monitor size={14} /> Total TVs</div>
          <div className="stat-card-value">{stats.devices?.total_devices || 0}</div>
          <div className="stat-card-sub">
            <span style={{ color: 'var(--success)' }}>{stats.devices?.online_devices || 0} online</span>
            {stats.devices?.pending_devices > 0 && (
              <span style={{ color: 'var(--warning)', marginLeft: 8 }}>{stats.devices.pending_devices} pending</span>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label"><ListVideo size={14} /> Playlists</div>
          <div className="stat-card-value">{stats.playlists?.total_playlists || 0}</div>
          <div className="stat-card-sub">{stats.playlists?.active_playlists || 0} active</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label"><FolderOpen size={14} /> Media Files</div>
          <div className="stat-card-value">{stats.media?.total_files || 0}</div>
          <div className="stat-card-sub">{formatBytes(stats.media?.total_storage_bytes)} used</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label"><Calendar size={14} /> Schedules</div>
          <div className="stat-card-value">{stats.schedules?.total_schedules || 0}</div>
          <div className="stat-card-sub">{stats.schedules?.active_schedules || 0} active</div>
        </div>
      </div>

      {/* Media breakdown */}
      {stats.media && (stats.media.video_count > 0 || stats.media.image_count > 0) && (
        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Media Breakdown</h3>
          <div style={{ display: 'flex', gap: 32 }}>
            <div><span style={{ fontSize: 24, fontWeight: 700 }}>{stats.media.video_count || 0}</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary)', marginLeft: 6 }}>Videos</span></div>
            <div><span style={{ fontSize: 24, fontWeight: 700 }}>{stats.media.image_count || 0}</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary)', marginLeft: 6 }}>Images</span></div>
            <div><span style={{ fontSize: 24, fontWeight: 700 }}>{stats.media.html_count || 0}</span>
              <span style={{ fontSize: 13, color: 'var(--text-tertiary)', marginLeft: 6 }}>HTML</span></div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="card">
        <div style={{ padding: '18px 22px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={16} />
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Recent Activity</h3>
        </div>
        {stats.recent_activity?.length > 0 ? (
          <div style={{ padding: '12px 6px' }}>
            {stats.recent_activity.map((log) => (
              <div key={log.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 16px', fontSize: 13,
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: log.action.includes('delete') ? 'var(--danger)' :
                    log.action.includes('create') || log.action.includes('upload') ? 'var(--success)' :
                    'var(--info)',
                }} />
                <span style={{ flex: 1, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {log.user_name || 'System'}
                  </span>
                  {' '}{log.action.replace(/_/g, ' ')}
                  {log.tv_number && <span style={{ color: 'var(--accent-text)' }}> ({log.tv_number})</span>}
                </span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: 12, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                  {formatTimeAgo(log.created_at)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state" style={{ padding: 40 }}>
            <p>No activity yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
